import React, { useEffect, useRef, useState } from "react";
import { ShoppingBag, MicOff, MessageCircle } from "lucide-react";
import { resolveTenantLogoUrl } from "../../../lib/retailFormat";
import CategoryTabs from "./CategoryTabs";
import CatalogFilterBar from "./CatalogFilterBar";
import ProductFamilyTabs from "./ProductFamilyTabs";
import SearchBar from "./SearchBar";
import { MenuSection, EmptyMenu } from "./MenuSection";
import FavoritesSection from "./FavoritesSection";
import CartPanel from "./CartPanel";
import { MobileCartButton } from "./MobileCartDrawer";

// Phase 3M — extracted verbatim from RetailCustomer.jsx::KioskShell +
// KioskHeader + KioskFooterBar. The IntersectionObserver scroll-spy and
// `sectionRefs` map live HERE (not in CategoryTabs/MenuSection) so that
// scrolling, section refs, and active-pill highlighting stay coupled.

function KioskHeader({ tenant, catalog, customer, orderType, labelFor, onNewOrder }) {
  const tenantName = tenant?.name || catalog?.name || "Our Kitchen";
  // See SalesOrderLanding — same single stored logo, resolved against
  // the API origin, with a graceful fallback when there is none (or the
  // stored file is gone).
  const [logoBroken, setLogoBroken] = useState(false);
  useEffect(() => { setLogoBroken(false); }, [tenant?.logo_url]);
  const logoSrc = logoBroken ? "" : resolveTenantLogoUrl(tenant?.logo_url || "");
  return (
    <header
      className="bg-[#0a1a0e] text-emerald-50 border-b-4 border-orange-500 px-4 sm:px-6 py-3 flex items-center gap-4 shrink-0"
      data-testid="retail-kiosk-header"
    >
      <div className="flex items-center gap-3 min-w-0">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={`${tenantName} logo`}
            className="w-10 h-10 rounded-md object-contain bg-black"
            data-testid="retail-header-logo"
            onError={() => setLogoBroken(true)}
          />
        ) : (
          <div
            className="w-10 h-10 rounded-md bg-emerald-900 grid place-items-center"
            data-testid="retail-header-logo-fallback"
            role="img"
            aria-label={`${tenantName} logo`}
          ><ShoppingBag className="w-4 h-4"/></div>
        )}
        <div className="min-w-0">
          <div className="font-serif text-lg leading-tight truncate" data-testid="retail-tenant-name">{tenantName}</div>
          {tenant?.tagline && <div className="text-[10px] uppercase tracking-widest text-emerald-200/70 truncate">{tenant.tagline}</div>}
        </div>
      </div>
      <div className="flex-1 text-center min-w-0">
        {customer?.name && (
          <span className="text-sm italic text-emerald-100/80">Hello, {customer.name} 👋</span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-emerald-200/70">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live Menu
        </span>
        <button
          type="button"
          onClick={onNewOrder}
          data-testid="retail-new-order-btn"
          className="text-xs sm:text-sm px-3 py-1.5 rounded-md bg-emerald-700/30 border border-emerald-300/20 text-emerald-100 hover:bg-emerald-700/50"
        >
          🆕 New Order
        </button>
        {orderType && (
          <span className="hidden md:inline text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-orange-500/20 text-orange-200 border border-orange-300/30" data-testid="retail-mode-pill">
            {labelFor ? labelFor(orderType) : ({ dine_in: "Dine-In", takeout: "Takeout", pickup: "Pickup", delivery: "Local Delivery" }[orderType] || orderType)}
          </span>
        )}
      </div>
    </header>
  );
}

function KioskFooterBar({ tenant, onChat }) {
  // Bottom assistant strip — text-only chat via the existing /chat endpoint.
  // Voice ordering is intentionally PAUSED for now and shows a static badge.
  // Iter41 — when the operator disables Chat Assistant on this catalog
  // (`onChat` is null), the entire footer bar is suppressed so the storefront
  // feels intentionally order-only rather than showing a disabled-state
  // placeholder.
  if (!onChat) return null;
  return (
    <div className="bg-[#0a1a0e] border-t border-emerald-900/40 px-3 sm:px-5 py-2 hidden md:flex items-center gap-3 text-emerald-100" data-testid="retail-footer-bar">
      <button
        type="button"
        onClick={onChat}
        data-testid="retail-footer-chat"
        className="w-10 h-10 rounded-full bg-emerald-900/60 grid place-items-center text-emerald-200 hover:bg-emerald-900"
        aria-label="Chat with us"
      >
        <MessageCircle className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={onChat}
        className="flex-1 text-left min-w-0 group"
      >
        <div className="text-xs uppercase tracking-wider text-emerald-300/80">Assistant</div>
        <div className="text-sm text-emerald-100/80 truncate group-hover:text-emerald-50">
          {tenant?.assistant_greeting || "Tap to chat — voice ordering is coming soon."}
        </div>
      </button>
      <button
        type="button"
        disabled
        title="Voice ordering is currently disabled"
        data-testid="retail-voice-btn"
        className="text-xs px-3 py-1.5 rounded-md bg-emerald-900/40 text-emerald-200/60 border border-emerald-200/15 cursor-not-allowed inline-flex items-center gap-1.5"
      >
        <MicOff className="w-3.5 h-3.5" /> Voice paused
      </button>
    </div>
  );
}

function NoFilteredResults({ onClear }) {
  return (
    <div className="grid place-items-center py-20" data-testid="retail-no-filtered-results">
      <div className="text-center px-6">
        <div className="text-base font-medium text-emerald-100">Nothing matched</div>
        <p className="text-sm text-emerald-200/60 mt-1">
          Try a different word, a brand, or a size.
        </p>
        <button
          type="button"
          onClick={onClear}
          data-testid="retail-no-filtered-results-clear"
          className="mt-4 h-11 px-4 rounded-md bg-orange-500 text-white text-sm font-semibold"
        >
          Show the full menu
        </button>
      </div>
    </div>
  );
}

// Shown when a chosen search suggestion CANNOT be opened at all.
//
// This is no longer the answer to "the card is not on this page" — that
// case now fetches the authoritative product and renders a real card
// (see `RetailCustomer::handleSelectProduct`). What remains here is the
// case that genuinely cannot be resolved: the operator withdrew the
// product between the suggestion being produced and the customer
// tapping it, or the id does not belong to this tenant and catalog.
// Never a silent dead tap: the customer is told plainly, and their own
// words stay on screen to try again.
function OffPageNotice({ label, onDismiss }) {
  return (
    <div
      role="status"
      data-testid="retail-search-offpage-notice"
      className="shrink-0 px-3 sm:px-5 py-2 bg-amber-900/30 border-b border-amber-500/30 flex items-center gap-3"
    >
      <p className="flex-1 min-w-0 text-sm text-amber-50">
        <span className="font-medium">{label}</span>{" "}
        is not available right now. Try another search, or browse the menu.
      </p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        data-testid="retail-search-offpage-dismiss"
        className="h-11 w-11 grid place-items-center text-amber-100 shrink-0"
      >
        ×
      </button>
    </div>
  );
}

// A single product opened from Search that the loaded browse page never
// contained. The banner names what is being shown and, crucially, gives
// one obvious way back to the catalog the customer was browsing.
function SearchResultBanner({ onBack }) {
  return (
    <div
      className="shrink-0 px-3 sm:px-5 py-2 bg-emerald-900/50 border-b border-emerald-400/30 flex items-center gap-3"
      data-testid="retail-search-result-banner"
    >
      <p className="flex-1 min-w-0 text-sm text-emerald-50">
        Showing one search result.
      </p>
      <button
        type="button"
        onClick={onBack}
        data-testid="retail-search-result-back"
        className="h-11 px-3 rounded-md bg-emerald-700/60 border border-emerald-300/25 text-sm font-medium text-emerald-50"
      >
        Back to menu
      </button>
    </div>
  );
}

function NoFilterMatches({ onClear }) {
  return (
    <div className="grid place-items-center py-16 px-4 text-center" data-testid="retail-filter-empty">
      <div className="text-emerald-50 text-sm font-medium">No products match these filters.</div>
      <button
        type="button"
        onClick={onClear}
        className="mt-3 min-h-[40px] px-4 rounded-md bg-orange-500 text-white text-sm font-semibold"
      >
        Clear all filters
      </button>
    </div>
  );
}

export default function KioskShell(props) {
  const {
    tenant, catalog, customer, orderType, categories,
    itemsInCart, cart, currency, cartCount,
    // Cart lines indexed by `(product_id, spice_level)` (Sep 2026,
    // restored). Optional: a caller that passes only `itemsInCart`
    // keeps today's product-id-only behaviour.
    cartLines,
    onAdd, onSetQty, onRemove, onCheckout, onMobileCart, onNewOrder,
    // Item-level Takeout marker (Sep 2026) — passed straight through to
    // the cart panel, which decides on its own whether to offer it.
    onSetItemTakeout,
    // Customer-selectable spice (Sep 2026, restored) — the catalog
    // switch, passed down to every card; each card still decides on its
    // own whether its product has a level to choose from.
    spiceEnabled = true,
    onChat, onInfo, labelFor,
    // Customer Favorites (Aug 2026)
    favoriteAccess, favoritesData, favoritedIds, onToggleFavorite,
    onRemoveFavorite, onRequireMembership,
    // Walk-in customer recognition/verification correction (Aug 2026)
    verified, onRequireVerification,
    // Sticky Search (Sep 2026). `search` is the `useProductSearch`
    // hook's own return value, owned by `RetailCustomer` ABOVE the cart
    // and favourites hooks — so typing can never remount either of them
    // and can never disturb the cart or a member's favourite state.
    // Omitting it renders no search bar at all, which is exactly what a
    // catalog with `search_enabled: false` gets.
    search, catalogTruncated = false, totalProducts,
    // Off-page product selection (Sep 2026). `RetailCustomer` owns the
    // decision and the authoritative fetch, because it holds the loaded
    // browse page and the cart/Favorites state; the shell only reveals
    // whatever it is told to. Every prop is optional, so a caller that
    // supplies none keeps the shell's own previous behaviour.
    onSelectProduct, revealTarget,
    selectedProduct, selectedProductLoading, selectedProductError,
    onClearSelectedProduct,
    // Product Family navigation (Sep 2026). Owned by `RetailCustomer`
    // for the same reason Search is: it scopes the categories, the
    // menu and the search box at once, so one component must hold it.
    // All three props are optional — a caller that passes none renders
    // no family row and behaves exactly as this shell does today.
    familyTabs, activeFamily, onFamilyChange,
    // Sales Order filters (Oct 2026) — the `useCatalogFilters` hook's
    // return value, owned by `RetailCustomer`. While a filter is active
    // the menu shows the server's filtered, paginated list of individual
    // products, and the Category row narrows that list instead of
    // scrolling. `navCategories` keeps the Category row listing the
    // family's categories while the menu shows filtered results. Both
    // optional: a caller that passes neither renders exactly as before.
    filters, navCategories,
  } = props;
  const filterActive = !!(filters && filters.active);

  const [activeCat, setActiveCat] = useState(categories[0]?.name || "");

  // Switching Product Family (or any other change that rebuilds the
  // category row) can remove the category the pill highlight is
  // pointing at. Reconcile rather than leave a highlight on a section
  // that is no longer on the page: fall back to the first category
  // that IS present. Only runs when the current selection has actually
  // become invalid, so an ordinary scroll-spy update is untouched.
  useEffect(() => {
    const names = (categories || []).map((c) => c.name);
    if (names.length === 0) {
      if (activeCat) setActiveCat("");
      return;
    }
    if (!names.includes(activeCat)) setActiveCat(names[0]);
  }, [categories, activeCat]);
  const sectionRefs = useRef({});
  // Per-product refs so a chosen suggestion can be scrolled to and
  // highlighted. Collected here (not in MenuSection) for the same
  // reason `sectionRefs` is: scrolling and the shell's own layout stay
  // coupled in one place.
  const productRefs = useRef({});
  const [highlightId, setHighlightId] = useState(null);
  // A product suggestion the customer chose whose card is not in the
  // loaded browse page (large, capped catalogs only). Held so the shell
  // can tell them instead of failing silently — see `revealProduct`.
  const [offPageSuggestion, setOffPageSuggestion] = useState(null);
  const highlightTimer = useRef(null);
  // Where the menu was before the customer started narrowing it, so
  // clearing Search puts them back rather than at the top.
  const mainRef = useRef(null);
  const restoreScroll = useRef(null);
  const filtering = !!(search && search.filtering);
  // The synthetic Favorites tab is deliberately never filtered out, so
  // `categories.length` alone cannot answer "did this search match
  // anything?" — for a Membership tenant it is never 0 and the customer
  // would be shown their Favorites with no word about the query that
  // matched nothing. Ask the real question instead.
  const noRenderableProducts = !categories.some(
    (cat) => !cat.isFavoritesTab && (cat.products || []).length > 0,
  );

  useEffect(() => () => {
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
  }, []);

  // Remember the scroll position the moment filtering starts, and put
  // the customer back there when they clear it — no unexplained jump.
  useEffect(() => {
    if (filtering) {
      if (restoreScroll.current === null && mainRef.current) {
        restoreScroll.current = mainRef.current.scrollTop;
      }
    } else if (restoreScroll.current !== null) {
      const top = restoreScroll.current;
      restoreScroll.current = null;
      if (mainRef.current) mainRef.current.scrollTop = top;
    }
  }, [filtering]);

  // Scroll-spy: track which section is dominant in the viewport for the
  // category-pill highlight. PAUSED while filtering — the visible
  // sections are a filtered subset, so spying on them would fight the
  // customer's own category selection.
  useEffect(() => {
    if (filtering) return;
    if (!("IntersectionObserver" in window)) return;
    const opts = { rootMargin: "-30% 0px -55% 0px", threshold: 0 };
    const obs = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      if (visible[0]) setActiveCat(visible[0].target.dataset.cat);
    }, opts);
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [categories, filtering]);

  const scrollToCat = (name) => {
    // Tapping a category is one predictable action: it clears any
    // active search first, then jumps — never two states at once.
    if (search && search.filtering) search.clear();
    const el = sectionRefs.current[name];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const revealProduct = (productId, onMissing) => {
    if (!productId) return;
    // Let the (re-rendered) list settle before measuring the card.
    requestAnimationFrame(() => {
      const el = productRefs.current[productId];
      if (!el) {
        // No card to reveal. When the page owns selection this cannot
        // normally happen — it fetches the product and renders it as a
        // focused result BEFORE asking for the reveal — so this is the
        // standalone-shell path, and it must still never be a silent
        // dead tap. See `OffPageNotice`.
        if (onMissing) onMissing();
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Move focus so a keyboard or screen-reader customer lands on
      // the card they just chose, not back at the top of the menu.
      try { el.focus({ preventScroll: true }); } catch (_e) { /* older browsers */ }
      setHighlightId(productId);
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      highlightTimer.current = setTimeout(() => setHighlightId(null), 1200);
    });
  };

  // The page tells the shell WHICH product to reveal, and bumps a nonce
  // so picking the same product twice reveals it again.
  const revealNonce = revealTarget && revealTarget.nonce;
  useEffect(() => {
    if (revealTarget && revealTarget.id) revealProduct(revealTarget.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealNonce]);

  const handleSelectProduct = (suggestion) => {
    // Selecting a product NEVER adds to cart — the customer confirms on
    // the card. When the page supplies its own handler it owns the whole
    // decision, including fetching a product this page does not contain
    // and deciding WHEN it is safe to clear the query.
    if (onSelectProduct) {
      setOffPageSuggestion(null);
      onSelectProduct(suggestion);
      return;
    }
    if (!search) return;
    search.clear();
    revealProduct(suggestion.id, () => setOffPageSuggestion(suggestion));
  };

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden flex flex-col lg:flex-row" data-testid="retail-kiosk">
      {/* Left side: header + scrollable menu + chat bar */}
      <div className="flex-1 min-w-0 flex flex-col lg:h-screen">
        <KioskHeader
          tenant={tenant}
          catalog={catalog}
          customer={customer}
          orderType={orderType}
          labelFor={labelFor}
          onNewOrder={onNewOrder}
        />

        {search && (
          <SearchBar
            query={search.query}
            onQueryChange={search.setQuery}
            suggestions={search.suggestions}
            loading={search.loading}
            open={search.open}
            onOpenChange={search.setOpen}
            onSelectProduct={handleSelectProduct}
            onSelectGroup={search.applyFacet}
            onClear={search.clear}
            facet={search.facet}
            onClearFacet={search.clearFacet}
            currency={currency}
            truncated={catalogTruncated}
            totalProducts={totalProducts}
          />
        )}

        <ProductFamilyTabs
          tabs={familyTabs}
          active={activeFamily}
          onChange={onFamilyChange}
        />

        <CategoryTabs
          categories={filterActive && navCategories ? navCategories : categories}
          active={filterActive ? filters.selection.category : activeCat}
          onJump={filterActive
            ? (name) => filters.setCategory(filters.selection.category === name ? null : name)
            : scrollToCat}
        />

        <CatalogFilterBar filters={filters} />

        {selectedProduct && onClearSelectedProduct && (
          <SearchResultBanner onBack={onClearSelectedProduct} />
        )}

        {selectedProductLoading && (
          <div
            role="status"
            data-testid="retail-search-opening"
            className="shrink-0 px-3 sm:px-5 py-2 bg-emerald-900/40 border-b border-emerald-400/20 text-sm text-emerald-50"
          >
            Opening…
          </div>
        )}

        {(offPageSuggestion || selectedProductError) && (
          <OffPageNotice
            label={selectedProductError || (offPageSuggestion && offPageSuggestion.label)}
            onDismiss={() => {
              setOffPageSuggestion(null);
              if (onClearSelectedProduct) onClearSelectedProduct();
            }}
          />
        )}

        <main ref={mainRef} className="flex-1 overflow-y-auto bg-[#0d1f12]/95 pb-32 lg:pb-24">
          {filterActive && !filters.loading && noRenderableProducts ? (
            <NoFilterMatches onClear={filters.clearAll} />
          ) : (filtering ? noRenderableProducts : categories.length === 0) ? (
            filtering ? <NoFilteredResults onClear={search.clear} /> : <EmptyMenu />
          ) : (
            categories.map((cat) =>
              cat.isFavoritesTab ? (
                <FavoritesSection
                  key={cat.name}
                  catName={cat.name}
                  sectionRef={(el) => { sectionRefs.current[cat.name] = el; }}
                  member={favoriteAccess === "live"}
                  verified={verified}
                  loaded={!!favoritesData?.loaded}
                  items={favoritesData?.items || []}
                  currency={currency}
                  itemsInCart={itemsInCart}
                  cartLines={cartLines}
                  onAdd={onAdd}
                  onSetQty={onSetQty}
                  onRemove={onRemove}
                  onInfo={onInfo}
                  spiceEnabled={spiceEnabled}
                  onToggleFavorite={onToggleFavorite}
                  onRemoveFavorite={onRemoveFavorite}
                  onRequireMembership={onRequireMembership}
                  onRequireVerification={onRequireVerification}
                />
              ) : (
                <MenuSection
                  key={cat.name}
                  catName={cat.name}
                  products={cat.products}
                  currency={currency}
                  itemsInCart={itemsInCart}
                  cartLines={cartLines}
                  onAdd={onAdd}
                  onSetQty={onSetQty}
                  onRemove={onRemove}
                  onInfo={onInfo}
                  spiceEnabled={spiceEnabled}
                  sectionRef={(el) => { sectionRefs.current[cat.name] = el; }}
                  registerProductRef={(id, el) => { productRefs.current[id] = el; }}
                  highlightId={highlightId}
                  favoriteAccess={favoriteAccess}
                  verified={verified}
                  favoritedIds={favoritedIds}
                  onToggleFavorite={onToggleFavorite}
                  onRequireMembership={onRequireMembership}
                  onRequireVerification={onRequireVerification}
                />
              )
            )
          )}
          {filterActive && filters.hasMore && (
            <div className="px-3 sm:px-5 py-4 text-center">
              <button
                type="button"
                onClick={filters.loadMore}
                disabled={filters.loading}
                data-testid="retail-filter-load-more"
                className="min-h-[44px] px-5 rounded-md bg-orange-500 text-white text-sm font-semibold disabled:opacity-60"
              >
                {filters.loading ? "Loading…" : `Show more (${filters.items.length} of ${filters.total})`}
              </button>
            </div>
          )}
        </main>

        <KioskFooterBar tenant={tenant} onChat={onChat} />
      </div>

      {/* Right side: persistent cart panel on desktop/tablet */}
      <CartPanel
        cart={cart}
        currency={currency}
        customer={customer}
        orderType={orderType}
        labelFor={labelFor}
        onSetQty={onSetQty}
        onRemove={onRemove}
        onSetItemTakeout={onSetItemTakeout}
        onCheckout={onCheckout}
      />

      {/* Mobile sticky cart button */}
      <MobileCartButton cartCount={cartCount} cart={cart} currency={currency} onClick={onMobileCart} />
    </div>
  );
}
